import { Button } from '@app/components/various/Button';
import { Form, FormButtons, FormGrid, FormNotification, InputField } from '@app/components/various/Form';
import { paths } from '@app/paths';
import { useLogin } from '@app/services/hooks/auth/useLogin';
import { useValidatedForm } from '@app/utils/hooks/useValidatedForm';
import { isDefined } from '@app/utils/is';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

interface FormData {
  email: string;
  password: string;
}

export const LoginForm = () => {
  const { t } = useTranslation(['common', 'login', 'passwordManagement', 'validation']);
  const { isError, isPending, mutate } = useLogin();
  const navigate = useNavigate();
  const location = useLocation();

  const validationSchema: yup.ObjectSchema<FormData> = yup.object({
    email: yup.string().defined().email(t('validation:invalid_email_hint')).required(t('validation:email_address_hint')),
    password: yup.string().required(t('validation:password_hint')),
  });

  const formMethods = useValidatedForm<FormData>({
    validationSchema,
  });

  const { register } = formMethods;

  const handleSubmit = async (values: FormData) => {
    mutate(values, {
      onSuccess: () => {
        if (!isDefined(location.state?.from)) {
          navigate(paths.ROOT);

          return;
        }
        navigate({
          pathname: location.state.from.pathname,
          search: location.state.from.search,
        });
      },
    });
  };

  return (
    <Form formMethods={formMethods} onSubmit={handleSubmit}>
      <FormGrid>
        <InputField type="email" label={t('common:email')} {...register('email')} />
        <InputField type="password" label={t('passwordManagement:password')} {...register('password')} />
      </FormGrid>
      {isError && <FormNotification message={t('login:sign_in_error')} />}
      <FormButtons direction="column">
        <Button type="submit" isLoading={isPending}>
          {t('login:sign_in')}
        </Button>
        <Button as={Link} to={paths.PASSWORD_FORGOT} color="secondary" variant="ghost">
          {t('passwordManagement:forgot_your_password')}
        </Button>
      </FormButtons>
    </Form>
  );
};
